/**
 * Workspace Initialization Service
 * 
 * Pure service functions for workspace initialization logic.
 * NO dependencies on React contexts/hooks to avoid circular dependencies.
 */

import { supabase } from '@/integrations/supabase/client';

export interface OnboardResponse {
  ok: boolean;
  workspace_id?: string;
  error?: string;
  message?: string;
}

/**
 * Get workspace_id from user profile (source of truth)
 */
export async function getWorkspaceIdFromProfile(userId: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('workspace_id')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('[workspaceInit.service] Error fetching profile:', error);
      return null;
    }

    return data?.workspace_id || null;
  } catch (err) {
    console.error('[workspaceInit.service] Unexpected error:', err);
    return null;
  }
}

/**
 * Initialize workspace via RPC
 */
export async function initializeWorkspace(): Promise<OnboardResponse> {
  try {
    console.log('[workspaceInit.service] Calling onboard_workspace RPC...');
    
    const { data, error: rpcError } = await supabase
      .rpc('onboard_workspace') as { data: OnboardResponse | null; error: any };

    if (rpcError) {
      console.error('[workspaceInit.service] RPC error:', rpcError);
      return {
        ok: false,
        error: rpcError.message || 'Failed to initialize workspace',
        message: rpcError.message || 'RPC_ERROR',
      };
    }

    if (!data) {
      console.error('[workspaceInit.service] No data returned from RPC');
      return {
        ok: false,
        error: 'No response from workspace initialization',
        message: 'NO_RESPONSE',
      };
    }

    console.log('[workspaceInit.service] RPC response:', data);
    return data;
  } catch (err: any) {
    console.error('[workspaceInit.service] Unexpected error:', err);
    return {
      ok: false,
      error: err?.message || 'Unexpected error during workspace initialization',
      message: 'UNEXPECTED_ERROR',
    };
  }
}

/**
 * Sync workspace_id from profile to ensure consistency
 */
export async function syncWorkspaceFromProfile(userId: string): Promise<string | null> {
  // First, get workspace_id from profile (source of truth)
  const profileWorkspaceId = await getWorkspaceIdFromProfile(userId);
  
  if (profileWorkspaceId) {
    return profileWorkspaceId;
  }

  // No workspace in profile - try to initialize
  const initResult = await initializeWorkspace();
  
  if (initResult.ok && initResult.workspace_id) {
    // Verify profile was updated (source of truth)
    const finalWorkspaceId = await getWorkspaceIdFromProfile(userId);
    return finalWorkspaceId || initResult.workspace_id;
  }

  return null;
}

